import { emit } from '../../platform/analytics/analytics'
import { useOnce } from '../../platform/analytics/useOnce'
import { Async } from '../../platform/async/Async'
import { Skeleton } from '../../platform/async/Skeleton'
import { DataGrid, type Column } from '../../platform/primitives/DataGrid'
import { Money } from '../../platform/primitives/Money'
import { Panel } from '../../platform/primitives/Panel'
import { useBreakpoint } from '../../platform/responsive/useBreakpoint'
import '../../platform/notes.css'
import { dayLabel, otherCurrencyNote, rangeLabel, transactionCountLabel } from './statementCycleLabels'
import type { StatementCycle, StatementCycles } from './statementCyclesWire'
import type { Account } from './useAccounts'
import { useStatementCycles } from './useStatementCycles'
import './accounts.css'

const WIDE_COLUMNS: Column<StatementCycle>[] = [
  {
    key: 'cycle',
    header: 'Cycle',
    width: 'minmax(0, 1.4fr)',
    render: (c) => rangeLabel(c.startDate, c.endDate),
  },
  {
    key: 'spent',
    header: 'Spent',
    width: 'minmax(max-content, 0.8fr)',
    align: 'end',
    render: (c) => <Money cents={c.spendCents} currency={c.currency} kind="stock" />,
  },
  {
    key: 'credits',
    header: 'Payments & refunds',
    width: 'minmax(max-content, 0.8fr)',
    align: 'end',
    render: (c) => <Money cents={c.creditsCents} currency={c.currency} kind="stock" />,
  },
  {
    key: 'count',
    header: 'Transactions',
    width: 'minmax(max-content, 0.5fr)',
    align: 'end',
    render: (c) => c.transactionCount,
  },
]

/** Phone: Cycle and Spent only. Credits and the count fold into the Cycle cell as a second line. */
const PHONE_COLUMNS: Column<StatementCycle>[] = [
  {
    key: 'cycle',
    header: 'Cycle',
    width: 'minmax(0, 1.4fr)',
    render: (c) => (
      <span className="statement-cycle__cycle-cell">
        <span>{rangeLabel(c.startDate, c.endDate)}</span>
        <span className="statement-cycle__meta">
          <span>
            <Money cents={c.creditsCents} currency={c.currency} kind="stock" /> payments & refunds
          </span>
          <span>{transactionCountLabel(c.transactionCount)}</span>
        </span>
      </span>
    ),
  },
  WIDE_COLUMNS[1] as Column<StatementCycle>,
]

/** The cycles before the open one, from the SAME response (no second request). */
function History({ cycles }: { cycles: StatementCycle[] }) {
  const phone = useBreakpoint() === 'phone'
  return (
    <>
      <h4 className="statement-cycle__history-title">Previous cycles</h4>
      <div className="statement-cycle__scroll">
        <DataGrid
          columns={phone ? PHONE_COLUMNS : WIDE_COLUMNS}
          groups={[{ label: '', rows: cycles }]}
          rowKey={(c) => c.endDate}
          label="Previous statement cycles"
          empty="No earlier cycles."
        />
      </div>
    </>
  )
}

/**
 * The open cycle, from the server's figures. Nothing here sums, nets or
 * derives: `Spent` and `Payments & refunds` are the two numbers the backend
 * sent, and they render `kind="stock"` (plain ink) because `credits` is an
 * absolute value and a green `flow` figure would read as income. The currency
 * is the CYCLE's own, not the account's.
 */
function CycleBody({ data }: { data: StatementCycles }) {
  // StrictMode runs effects twice in dev; `useOnce` keeps the view to one event.
  useOnce(() => emit('statement_cycles_viewed', { cycle_count: data.cycles.length }))
  // ONE partition: the open cycle is `isCurrent` wherever the server put it;
  // history is every other cycle, in the server's newest-first order.
  const open = data.cycles.find((c) => c.isCurrent) ?? data.cycles[0]
  const history = data.cycles.filter((c) => c !== open)
  if (open === undefined) return null
  const otherCurrency = otherCurrencyNote(open.otherCurrencyCount)
  return (
    <>
      <div className="statement-cycle__dates">
        <span className="statement-cycle__range">{rangeLabel(open.startDate, open.endDate)}</span>
        <span className="note">{`Statement closes ${dayLabel(open.endDate)}`}</span>
      </div>
      <dl className="statement-cycle__rows">
        <div className="account-detail__row account-detail__row--inline">
          <dt className="account-detail__row-label">Spent</dt>
          <dd className="account-detail__row-value statement-cycle__value">
            <Money cents={open.spendCents} currency={open.currency} kind="stock" />
          </dd>
        </div>
        <div className="account-detail__row account-detail__row--inline">
          <dt className="account-detail__row-label">Payments & refunds</dt>
          <dd className="account-detail__row-value statement-cycle__value">
            <Money cents={open.creditsCents} currency={open.currency} kind="stock" />
          </dd>
        </div>
        <div className="account-detail__row account-detail__row--inline">
          <dt className="account-detail__row-label">Transactions</dt>
          <dd className="account-detail__row-value statement-cycle__value">
            {transactionCountLabel(open.transactionCount)}
          </dd>
        </div>
      </dl>
      <p className="note">Pending transactions aren't counted until they post.</p>
      {otherCurrency !== null && <p className="note">{otherCurrency}</p>}
      {history.length > 0 && <History cycles={history} />}
    </>
  )
}

/**
 * "This cycle" on a credit card's detail page. Renders NOTHING (no heading, no
 * Panel shell) for a non-credit account and for a 404 or not-credit answer; an
 * account with no statement day shows the empty state and makes no request.
 * Opaque `Panel`, no `--accent` fill: the hero is this content area's lime tile.
 */
export function StatementCycleCard({ account, onSetStatementDay }: { account: Account; onSetStatementDay?: () => void }) {
  const { visible, state } = useStatementCycles(account)
  if (!visible) return null
  if (state.status === 'success' && state.data.kind === 'hidden') return null
  return (
    <Panel>
      <h3 className="account-form__title">This cycle</h3>
      <Async
        surface="statement-cycles"
        state={state}
        skeleton={
          <>
            <Skeleton height={20} />
            <Skeleton height={44} />
            <Skeleton height={44} />
          </>
        }
        empty={{
          title: 'No statement day set',
          body: 'Choose your statement day in Account settings to see what you spent each statement cycle.',
          // Async's action is the chip button, not lime: the hero holds this area's fill (rule 9).
          ...(onSetStatementDay === undefined ? {} : { action: { label: 'Set statement day', onAct: onSetStatementDay } }),
        }}
      >
        {(result) => (result.kind === 'cycles' ? <CycleBody data={result.data} /> : null)}
      </Async>
    </Panel>
  )
}
